import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API from '../services/api';
import { SkeletonCard } from '../components/ui/Skeleton';
import Icon from '../components/ui/Icon';
import EmptyState from '../components/ui/EmptyState';
import ErrorState from '../components/ui/ErrorState';
import QuestionImage from '../components/ui/QuestionImage';
import QuestionText from '../components/ui/QuestionText';
import { hasTable } from '../utils/questionText';
import './TestReview.css';

// "What happened in the assessment/session I just completed" — separate from Mistake Bank (which answers "what do I
// repeatedly need to fix", across all history). Scoped to ONE completed attempt/session, every question in it, correct
// or not. Read-only: never re-grades, never writes anything. `mode` picks which of the two review endpoints backs this
// screen — same shape from both, so one presentational screen covers them.
const STATUS_ICON = { correct: 'success', wrong: 'error', skipped: 'skipped' };
const STATUS_LABEL = { correct: 'Correct', wrong: 'Wrong', skipped: 'Skipped' };

const TestReview = ({ mode }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | correct | wrong | skipped

  const load = () => {
    setLoading(true); setError(null);
    const endpoint = mode === 'official' ? `/tests/${id}/review` : `/mock/session/${id}/review`;
    API.get(endpoint).then(res => setData(res.data)).catch(setError).finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [id, mode]);

  if (loading) {
    return <div className="z-page z-page--narrow" role="status" aria-label="Loading review"><SkeletonCard lines={2} /><SkeletonCard lines={4} /><SkeletonCard lines={4} /></div>;
  }
  if (error || !data) {
    return <div className="z-page z-page--narrow"><ErrorState error={error} onRetry={load} /></div>;
  }

  const items = data.review || [];
  const statusOf = (it) => (it.skipped ? 'skipped' : it.is_correct ? 'correct' : 'wrong');
  const filtered = items.filter(it => filter === 'all' || statusOf(it) === filter);
  const counts = {
    correct: items.filter(it => statusOf(it) === 'correct').length,
    wrong: items.filter(it => statusOf(it) === 'wrong').length,
    skipped: items.filter(it => statusOf(it) === 'skipped').length,
  };

  return (
    <div className="z-page z-page--narrow trv-body">
      <p className="trv-sub">Every question from this attempt, with the right answer and explanation.</p>

      <div className="z-segments trv-filters" role="tablist" aria-label="Filter questions">
        {[
          { key: 'all', label: `All (${items.length})` },
          { key: 'correct', label: `Correct (${counts.correct})` },
          { key: 'wrong', label: `Wrong (${counts.wrong})` },
          { key: 'skipped', label: `Skipped (${counts.skipped})` },
        ].map(f => (
          <button key={f.key} type="button" role="tab" aria-selected={filter === f.key} className="z-segment" onClick={() => setFilter(f.key)}>{f.label}</button>
        ))}
      </div>

      <div className="trv-list">
        {filtered.map((it, idx) => <ReviewItem key={it.question_id || idx} item={it} index={items.indexOf(it) + 1} statusOf={statusOf} />)}
        {filtered.length === 0 && <EmptyState icon="list" art={false} tone="neutral" compact title="Nothing in this category" body="Try another filter." />}
      </div>

      <button type="button" className="z-btn z-btn--secondary z-btn--block" onClick={() => navigate('/student/practice', { replace: true })}>
        <Icon name="practice" size={18} /> Back to Practice
      </button>
    </div>
  );
};

const ReviewItem = ({ item, index, statusOf }) => {
  const [open, setOpen] = useState(false);
  const status = statusOf(item);

  return (
    <div className={`trv-item trv-item--${status}`}>
      <button type="button" className="trv-item-head" onClick={() => setOpen(o => !o)} aria-expanded={open}>
        <span className={`trv-item-status trv-item-status--${status}`}><Icon name={STATUS_ICON[status]} size={22} title={STATUS_LABEL[status]} /></span>
        <span className="trv-item-main">
          <span className="trv-item-num">Question {index}</span>
          <span className="trv-item-text">{item.question_text}</span>
        </span>
        <Icon name={open ? 'chevron-up' : 'chevron-down'} size={18} className="trv-item-chev" />
      </button>

      {open && (
        <div className="trv-item-body">
          {/* The header line above is a one-line summary; a question that embeds a table needs its full text here. */}
          {hasTable(item.question_text) && <div className="trv-item-fulltext"><QuestionText text={item.question_text} /></div>}
          <QuestionImage path={item.image_path} />
          <div className="trv-options">
            {(item.options || []).map((opt, i) => {
              const optNum = i + 1;
              const isSelected = item.selected_option === optNum;
              const isCorrectOpt = String(opt).trim().toLowerCase() === String(item.correct_answer).trim().toLowerCase();
              let cls = 'trv-opt';
              if (isCorrectOpt) cls += ' trv-opt--correct';
              else if (isSelected) cls += ' trv-opt--wrong';
              return (
                <div key={i} className={cls}>
                  <span className="trv-opt-letter">{String.fromCharCode(65 + i)}</span>
                  <span className="trv-opt-text">{typeof opt === 'object' ? (opt.option_text ?? opt.text ?? '') : opt}</span>
                  {isSelected && <span className="trv-opt-tag">Your answer</span>}
                  {isCorrectOpt && !isSelected && <span className="trv-opt-tag">Correct</span>}
                </div>
              );
            })}
          </div>

          {item.explanation && (
            <div className="trv-explanation">
              <span className="trv-explanation-label"><Icon name="tip" size={16} /> Why</span>
              <p>{item.explanation}</p>
            </div>
          )}

          <div className="trv-meta">
            {item.time_taken_seconds != null && <span className="z-chip">{item.time_taken_seconds}s spent</span>}
            {item.option_change_count > 0 && <span className="z-chip">Changed answer {item.option_change_count}×</span>}
            {item.topic_name && <span className="z-chip z-chip--violet">{item.topic_name}</span>}
          </div>
        </div>
      )}
    </div>
  );
};

export default TestReview;
